import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import { Entity } from '../types/simulation';
import { EntityInterpolationData } from '../hooks/useSimulation';
import { stringToColor } from '../utils/mathHelpers';
import './Scene3D.css';

interface Scene3DProps {
  entities: Entity[];
  myEntityId: string | null;
  getInterpolationData: () => Map<string, EntityInterpolationData>;
  onCameraRotationChange?: (rotation: { yaw: number; pitch: number }) => void;
  fullscreenContainerRef?: React.RefObject<HTMLDivElement>;
  localPosition?: number[];
}

export function Scene3D({ entities, myEntityId, getInterpolationData, onCameraRotationChange, localPosition }: Scene3DProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const controlsRef = useRef<PointerLockControls>();
  const entityMeshes = useRef<Map<string, THREE.Group>>(new Map());
  const rotationRef = useRef({ yaw: 0, pitch: 0 });
  const entityPositions = useRef<Map<string, THREE.Vector3>>(new Map());
  const getInterpolationDataRef = useRef(getInterpolationData);
  const myEntityIdRef = useRef(myEntityId);

  // Keep refs up to date
  useEffect(() => {
    getInterpolationDataRef.current = getInterpolationData;
    myEntityIdRef.current = myEntityId;
  }, [getInterpolationData, myEntityId]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a1a);
    sceneRef.current = scene;

    // Camera - First person view
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 1.6, 0); // Eye level height
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // PointerLockControls
    const controls = new PointerLockControls(camera, renderer.domElement);
    controlsRef.current = controls;

    // Rotation is now updated in the animation loop (more reliable than change event)

    const handleClick = () => {
      controls.lock();
    };

    const handleLock = () => {
      document.body.classList.add('pointer-locked');
    };

    const handleUnlock = () => {
      document.body.classList.remove('pointer-locked');
    };

    renderer.domElement.addEventListener('click', handleClick);
    controls.addEventListener('lock', handleLock);
    controls.addEventListener('unlock', handleUnlock);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 20, 10);
    scene.add(directionalLight);

    // Grid
    const gridHelper = new THREE.GridHelper(40, 40, 0x444444, 0x222222);
    scene.add(gridHelper);

    // Axes
    const axesHelper = new THREE.AxesHelper(10);
    scene.add(axesHelper);

    // Animation loop with interpolation
    let animationId: number;
    function animate() {
      animationId = requestAnimationFrame(animate);

      // Update camera rotation on every frame (PointerLockControls change event is unreliable)
      if (onCameraRotationChange) {
        const direction = new THREE.Vector3();
        camera.getWorldDirection(direction);
        const newYaw = Math.atan2(-direction.x, -direction.z);
        const newPitch = Math.asin(-direction.y);

        // Only update if changed significantly to avoid unnecessary re-renders
        if (Math.abs(newYaw - rotationRef.current.yaw) > 0.001 ||
            Math.abs(newPitch - rotationRef.current.pitch) > 0.001) {
          rotationRef.current.yaw = newYaw;
          rotationRef.current.pitch = newPitch;
          onCameraRotationChange(rotationRef.current);
        }
      }

      // Interpolate entity positions
      const interpolationSpeed = 0.15;
      const interpolationData = getInterpolationDataRef.current();
      entityMeshes.current.forEach((group, entityId) => {
        // Don't interpolate player entity
        if (entityId === myEntityIdRef.current) return;

        const interpData = interpolationData.get(entityId);
        if (!interpData) return;

        const currentPos = entityPositions.current.get(entityId);
        if (!currentPos) {
          // Initialize position
          entityPositions.current.set(
            entityId,
            new THREE.Vector3(
              interpData.targetPosition.x,
              interpData.targetPosition.y,
              interpData.targetPosition.z
            )
          );
          return;
        }

        // Lerp towards target
        currentPos.lerp(
          new THREE.Vector3(
            interpData.targetPosition.x,
            interpData.targetPosition.y,
            interpData.targetPosition.z
          ),
          interpolationSpeed
        );

        // Update mesh position
        group.position.copy(currentPos);
      });

      renderer.render(scene, camera);
    }
    animate();

    // Handle window resize
    const handleResize = () => {
      if (!mountRef.current || !camera || !renderer) return;

      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('click', handleClick);
      controls.removeEventListener('lock', handleLock);
      controls.removeEventListener('unlock', handleUnlock);
      controls.unlock();
      controls.dispose();
      document.body.classList.remove('pointer-locked');
      cancelAnimationFrame(animationId);
      renderer.dispose();
      if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [onCameraRotationChange]);

  // Update entities in the scene
  useEffect(() => {
    if (!sceneRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;

    // Remove entities no longer present
    const currentIds = new Set(entities.map((e) => e.id));
    entityMeshes.current.forEach((group, id) => {
      if (!currentIds.has(id)) {
        scene.remove(group);
        entityMeshes.current.delete(id);
      }
    });

    // Add or update entities
    entities.forEach((entity) => {
      let group = entityMeshes.current.get(entity.id);
      const isMe = entity.id === myEntityId;

      if (!group) {
        // Create new entity group
        group = new THREE.Group();

        // Only create visible mesh for other entities (not for player)
        if (!isMe) {
          // Create entity mesh (sphere)
          const geometry = new THREE.SphereGeometry(0.5, 32, 32);
          const material = new THREE.MeshStandardMaterial({
            color: stringToColor(entity.id),
          });
          const mesh = new THREE.Mesh(geometry, material);
          group.add(mesh);

          // Add label using sprite
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (context) {
            canvas.width = 256;
            canvas.height = 64;
            context.fillStyle = 'rgba(0, 0, 0, 0.7)';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.font = 'bold 24px Arial';
            context.fillStyle = '#ffffff';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(entity.id, canvas.width / 2, canvas.height / 2);

            const texture = new THREE.CanvasTexture(canvas);
            const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
            const sprite = new THREE.Sprite(spriteMaterial);
            sprite.scale.set(2, 0.5, 1);
            sprite.position.set(0, 1.5, 0);
            group.add(sprite);
          }
        }

        scene.add(group);
        entityMeshes.current.set(entity.id, group);
      }

      // Update position
      if (isMe) {
        // Player entity: update immediately
        group.position.set(entity.position.x, entity.position.y, entity.position.z);
        // Update camera position to follow player entity (first-person)
        if (camera) {
          camera.position.set(entity.position.x, entity.position.y + 1.6, entity.position.z);
        }
      } else {
        // Other entities: only update if not already tracking for interpolation
        if (!entityPositions.current.has(entity.id)) {
          group.position.set(entity.position.x, entity.position.y, entity.position.z);
          entityPositions.current.set(
            entity.id,
            new THREE.Vector3(entity.position.x, entity.position.y, entity.position.z)
          );
        }
      }
    });
  }, [entities, myEntityId, cameraRef]);

  // Update camera position when using local position (not connected)
  useEffect(() => {
    if (localPosition && myEntityId === 'local' && cameraRef.current) {
      cameraRef.current.position.set(localPosition[0], localPosition[1] + 1.6, localPosition[2]);
    }
  }, [localPosition, myEntityId]);

  return <div ref={mountRef} className="scene-3d" />;
}
